import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, KeyRound, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import Logo from '../components/Logo.js';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  // mode: 'password' | 'token'
  const [mode, setMode] = useState('password');
  const [loading, setLoading] = useState(false);
  const [generatedToken, setGeneratedToken] = useState('');
  const navigate = useNavigate();

  const persistSession = (data) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('userRole', data.user.role);
    localStorage.setItem('userId', data.user.id);
    localStorage.setItem('userName', data.user.name || data.user.company || '');
    localStorage.setItem('userCompany', data.user.company || '');
    localStorage.setItem('userEmail', data.user.email || '');
  };

  // Login direto: tenta senha primeiro; se a conta não tem senha, faz fallback automático para token.
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Informe o email'); return; }
    if (!password) { toast.error('Informe a senha'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      persistSession(data);
      toast.success('Login realizado com sucesso');
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Email ou senha inválidos';
      // Se 401, oferece fallback para token
      if (err.response?.status === 401) {
        try {
          const check = await axios.post(`${API}/auth/has-password`, { email });
          if (check.data.exists && !check.data.has_password) {
            // conta sem senha definida → envia token automaticamente
            const resp = await axios.post(`${API}/auth/request-token`, { email });
            setGeneratedToken(resp.data.token);
            setMode('token');
            toast.info('Conta ainda sem senha. Use o token gerado.');
            return;
          }
          if (!check.data.exists) {
            toast.error('Email não cadastrado');
            return;
          }
        } catch { /* ignore */ }
      }
      toast.error(detail);
    } finally { setLoading(false); }
  };

  const requestToken = async () => {
    if (!email.trim()) { toast.error('Informe o email'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/request-token`, { email });
      setGeneratedToken(data.token);
      setMode('token');
      toast.success('Token gerado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar token');
    } finally { setLoading(false); }
  };

  const handleVerifyToken = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-token`, { email, token });
      persistSession(data);
      toast.success('Login realizado com sucesso');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Token inválido');
    } finally { setLoading(false); }
  };

  const backToPassword = () => { setMode('password'); setToken(''); setGeneratedToken(''); };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <Logo size="large" className="justify-center mb-2" />
            <p className="text-slate-600 mt-4">Plataforma ESG &amp; Workforce Intelligence</p>
          </div>

          {mode === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-5" data-testid="password-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email corporativo</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email" type="email" placeholder="seu@email.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required data-testid="email-input"
                    className="h-12 pl-10" autoFocus autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Senha</Label>
                  <button type="button" onClick={requestToken} className="text-xs text-accent hover:underline" data-testid="forgot-password-link">
                    Esqueci minha senha
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password" type="password" placeholder="Sua senha" value={password}
                    onChange={(e) => setPassword(e.target.value)} required data-testid="password-input"
                    className="h-12 pl-10" autoComplete="current-password"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-12" disabled={loading} data-testid="login-button">
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>

              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center"><span className="px-3 bg-white text-[10px] uppercase tracking-wider text-slate-400">ou</span></div>
              </div>

              <Button type="button" variant="outline" className="w-full h-11 gap-2" onClick={requestToken} disabled={loading} data-testid="request-token-button">
                <KeyRound className="w-4 h-4" /> Entrar com token (sem senha)
              </Button>

              <p className="text-xs text-center text-slate-500">
                Não possui cadastro? <a href="/cadastro" className="text-accent hover:underline" data-testid="register-link">Cadastre sua empresa</a>
              </p>
            </form>
          )}

          {mode === 'token' && (
            <form onSubmit={handleVerifyToken} className="space-y-5" data-testid="token-form">
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <Mail className="w-3 h-3" /> {email}
                <button type="button" onClick={backToPassword} className="ml-auto text-accent hover:underline flex items-center gap-1" data-testid="back-to-password-button">
                  <ArrowLeft className="w-3 h-3" /> usar senha
                </button>
              </div>

              {generatedToken && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center" data-testid="generated-token-display">
                  <p className="text-xs text-blue-600 font-medium mb-1">Seu token de acesso (modo demo)</p>
                  <p className="text-3xl font-mono font-bold tracking-widest text-slate-900">{generatedToken}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="token">Token de 6 dígitos</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="token" type="text" placeholder="000000" value={token}
                    onChange={(e) => setToken(e.target.value)} required data-testid="token-input"
                    className="h-12 text-center text-2xl tracking-widest font-mono pl-10" maxLength={6}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-12" disabled={loading} data-testid="verify-token-button">
                {loading ? 'Verificando...' : 'Entrar com Token'}
              </Button>
            </form>
          )}

          <div className="pt-6 border-t border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Mail className="w-4 h-4" />
              <span>Senha definida pela empresa · token via email (demo)</span>
            </div>
          </div>
        </div>
      </div>

      <div
        className="hidden lg:flex flex-1 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtZWV0aW5nJTIwZGl2ZXJzZSUyMHRlYW18ZW58MHx8fHwxNzcxNTQ0NjY2fDA&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="absolute inset-0 bg-primary/80 flex items-center justify-center p-12">
          <div className="text-white max-w-xl">
            <h2 className="text-4xl font-bold mb-6">Benchmark ESG &amp; RH Inteligente</h2>
            <p className="text-lg text-slate-200 leading-relaxed">
              Análise institucional, indicadores de RH, pesquisa salarial e governança estatística em uma única plataforma.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
